import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../lib/api";
import { useCompany } from "../lib/company";
import { Button, Card, Icon, cx } from "../components/ui";
import { formatNullableAuDate } from "../domain/time";
import { caughtErrorMessage } from "../domain/errors";
import {
  CLIENT_ROLE_OPTIONS,
  clientRoleDisplayName,
  clientRoleForDisplay,
  emptyTeamUserCreateFormState,
  emptyTeamUserSubmissionState,
  nextTeamUserCreateFormState,
  nextTeamUserSubmissionState,
  teamCompanyUserCountLabel,
  teamPlanDisplayName,
  teamPlanEntitlementIconName,
  teamPlanEntitlementLabel,
  teamPlanEntitlementState,
  teamPlanEntitlementToneClass,
  teamUserCreateFormSubmittedState,
  teamUserCreateFormView,
  teamUserStatusForActive,
  teamUserStatusLabel,
  teamUserStatusToggleCommand,
  teamUserStatusToggleLabel,
  teamUserStatusToneClass,
  teamUserNoticeToneClass,
  type TeamUserCreateFormAction,
} from "../domain/team";

type TeamDetail = NonNullable<FunctionReturnType<typeof api.users.teamForCompany>>;
type TeamUser = TeamDetail["users"][number];

export function Team() {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const detail = useQuery(api.users.teamForCompany, companyId !== null ? { companyId } : "skip");
  const createUser = useMutation(api.users.createClientUser);
  const setUserActive = useMutation(api.users.setClientUserActive);

  const [userDraft, setUserDraft] = useState(() => emptyTeamUserCreateFormState());
  const [submission, setSubmission] = useState(() => emptyTeamUserSubmissionState());
  const busy = submission.busy;
  const notice = submission.notice;
  const userFormView = useMemo(
    () => teamUserCreateFormView({
      draft: userDraft,
      entitled: detail?.entitled ?? false,
      canManage: detail?.canManage ?? false,
      busy,
    }),
    [busy, detail?.canManage, detail?.entitled, userDraft],
  );

  const updateUserDraft = (action: TeamUserCreateFormAction) => {
    setUserDraft((current) => nextTeamUserCreateFormState(current, action));
  };

  if (companyId === null) return <p className="px-9 py-7 text-slate-500">No company selected.</p>;
  if (detail === undefined) return <p className="px-9 py-7 text-slate-500">Loading team...</p>;
  if (detail === null) return <p className="px-9 py-7 text-slate-500">Team not found.</p>;

  const submitUser = async () => {
    if (!userFormView.canSubmit || !userFormView.prepared.ok) return;
    setSubmission((current) => nextTeamUserSubmissionState(current, { type: "started" }));
    try {
      await createUser({ companyId, ...userFormView.prepared.value });
      setUserDraft(teamUserCreateFormSubmittedState());
      setSubmission((current) => nextTeamUserSubmissionState(current, { type: "succeeded" }));
    } catch (e) {
      setSubmission((current) => nextTeamUserSubmissionState(current, {
        type: "failed",
        error: caughtErrorMessage(e, "Could not add user."),
      }));
    }
  };

  const toggleUser = async (user: TeamUser) => {
    const command = teamUserStatusToggleCommand({
      userId: user._id,
      active: user.active,
      entitled: detail.entitled,
      canManage: detail.canManage,
    });
    if (command.kind === "blocked") return;

    await setUserActive({ companyId, userId: command.userId, active: command.active });
  };

  return (
    <div className="mx-auto w-full max-w-[1080px] px-6 py-7 lg:px-9">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-[var(--accent-dark)]">{detail.company.name}</div>
          <h2 className="mt-1 font-serif text-[29px] font-semibold tracking-tight text-slate-950">Team access</h2>
          <p className="mt-1 max-w-[760px] text-[14px] leading-relaxed text-slate-500">
            Manage the client users who can access this company workspace.
          </p>
        </div>
        <PlanPill entitled={detail.entitled} planName={teamPlanDisplayName({ planName: detail.plan?.name, companyStatus: detail.company.status })} />
      </div>

      {!detail.entitled && (
        <div className="mb-5 overflow-hidden rounded-xl bg-slate-950 text-white ring-1 ring-slate-900">
          <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-5">
            <div>
              <div className="mb-1 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-300"><Icon name="lock" size={13} /> PREMIUM</div>
              <h3 className="text-[18px] font-semibold">Team management is a premium feature.</h3>
              <p className="mt-1 max-w-[620px] text-[13px] leading-relaxed text-slate-300">Upgrade to add client users from the portal. Existing users remain visible below.</p>
            </div>
            <Button className="!bg-white !text-slate-900 hover:!bg-slate-100" onClick={() => navigate("/subscriptions")}>View plans</Button>
          </div>
        </div>
      )}

      <section className="grid gap-5 lg:grid-cols-[1fr_22rem]">
        <Card className="overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
            <div>
              <h3 className="text-[14px] font-semibold text-slate-900">Users</h3>
              <p className="mt-0.5 text-[12.5px] text-slate-400">{teamCompanyUserCountLabel(detail.users.length)}</p>
            </div>
          </div>
          {detail.users.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">No client users have been added yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {detail.users.map((user) => {
                const clientRole = clientRoleForDisplay(user.role);
                const userStatus = teamUserStatusForActive(user.active);
                return (
                  <div key={user._id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-200">
                      <Icon name="shield" size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13.5px] font-semibold text-slate-800">{user.name ?? user.email}</div>
                      <div className="mt-0.5 flex flex-wrap gap-2 text-[12px] text-slate-400">
                        <span>{user.email}</span>
                        <span>/</span>
                        <span>{clientRoleDisplayName(clientRole)}</span>
                        <span>/</span>
                        <span>{formatNullableAuDate(user.accountExpiresAt, "No expiry")}</span>
                      </div>
                    </div>
                    <span className={cx("rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1", teamUserStatusToneClass(userStatus))}>
                      {teamUserStatusLabel(userStatus)}
                    </span>
                    {detail.canManage && (
                      <Button size="sm" variant="outline" disabled={!detail.entitled} onClick={() => toggleUser(user)}>
                        {teamUserStatusToggleLabel(userStatus)}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-[14px] font-semibold text-slate-900">Add user</h3>
          <p className="mt-1 text-[12.5px] leading-snug text-slate-400">
            Creates or updates the Lawstream access record. Invitation emails are not sent from this screen.
          </p>
          <div className="mt-4 grid gap-2">
            <label htmlFor="team-user-email" className="text-[12px] font-medium text-slate-600">Email address</label>
            <input id="team-user-email" type="email" autoComplete="email" className="rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400" placeholder="name@company.com" value={userDraft.email} disabled={userFormView.controlsDisabled} onChange={(e) => updateUserDraft({ type: "emailInput", value: e.target.value })} />
            <label htmlFor="team-user-name" className="text-[12px] font-medium text-slate-600">Name</label>
            <input id="team-user-name" autoComplete="name" className="rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400" placeholder="Full name" value={userDraft.name} disabled={userFormView.controlsDisabled} onChange={(e) => updateUserDraft({ type: "nameInput", value: e.target.value })} />
            <label htmlFor="team-user-role" className="text-[12px] font-medium text-slate-600">Role</label>
            <select id="team-user-role" className="rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400" value={userDraft.role} disabled={userFormView.controlsDisabled} onChange={(e) => updateUserDraft({ type: "roleInput", value: e.target.value })}>
              {CLIENT_ROLE_OPTIONS.map((option) => <option key={option} value={option}>{clientRoleDisplayName(option)}</option>)}
            </select>
            {userFormView.problem !== null && <div className="text-[12.5px] text-amber-700">{userFormView.problem}</div>}
            <Button icon={userFormView.submitButton.icon} disabled={userFormView.submitButton.disabled} onClick={submitUser}>{userFormView.submitButton.label}</Button>
            {notice !== null && <div className={cx("text-[12.5px]", teamUserNoticeToneClass(notice))}>{notice.text}</div>}
            {!detail.canManage && <div className="text-[12.5px] text-slate-400">Only client administrators can add users.</div>}
          </div>
        </Card>
      </section>
    </div>
  );
}

function PlanPill({ entitled, planName }: { entitled: boolean; planName: string }) {
  const state = teamPlanEntitlementState(entitled);
  return (
    <div className={cx("inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-semibold ring-1", teamPlanEntitlementToneClass(state))}>
      <Icon name={teamPlanEntitlementIconName(state)} size={15} />
      {teamPlanEntitlementLabel(state, planName)}
    </div>
  );
}
